import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetNotifications, useMarkNotificationRead } from '../../api/notificationsApi';
import { useSocket } from '../../context/SocketContext';
import { useSuppressTutorialTips } from '../../context/TutorialContext';
import { useActiveLeague } from '../../hooks/useActiveLeague';
import { useCurrentTeamId } from '../../hooks/useCurrentTeamId';
import TransferRevealModal from './TransferRevealModal';
import { TransferRevealSuppressionContext } from './TransferRevealContext';
import { isTransferRevealNotification, sortNotificationsOldestFirst } from './transferRevealUtils';

function TransferRevealProvider({ children, teamName }) {
  const navigate = useNavigate();
  const { activeLeague } = useActiveLeague();
  const currentTeamId = useCurrentTeamId();
  const socketCtx = useSocket();
  const activeLeagueSlug = activeLeague?.slug || null;
  const [queue, setQueue] = useState([]);
  const [shownCount, setShownCount] = useState(0);
  const [isSquadRevealOpen, setSquadRevealOpen] = useState(false);
  const [isDocumentVisible, setDocumentVisible] = useState(() => (
    typeof document === 'undefined' ? true : document.hidden !== true
  ));
  const seenNotificationIdsRef = useRef(new Set());
  const { mutate: markNotificationRead } = useMarkNotificationRead();
  const { data: unreadNotificationsData } = useGetNotifications({
    leagueSlug: activeLeagueSlug,
    read: false,
    type: 'transfer',
    enabled: Boolean(activeLeagueSlug),
  });

  const enqueueNotifications = useCallback((notifications) => {
    if (!Array.isArray(notifications) || notifications.length === 0) return;

    const eligible = notifications.filter((notification) => (
      isTransferRevealNotification(notification, activeLeagueSlug)
      && !seenNotificationIdsRef.current.has(String(notification._id))
    ));
    if (eligible.length === 0) return;

    eligible.forEach((notification) => {
      seenNotificationIdsRef.current.add(String(notification._id));
    });
    setQueue((currentQueue) => {
      const sortedEligible = sortNotificationsOldestFirst(eligible);
      return currentQueue.length === 0
        ? sortedEligible
        : [...currentQueue, ...sortedEligible];
    });
  }, [activeLeagueSlug]);

  useEffect(() => {
    setQueue([]);
    setShownCount(0);
    seenNotificationIdsRef.current.clear();
  }, [activeLeagueSlug]);

  useEffect(() => {
    const firstPage = unreadNotificationsData?.pages?.[0];
    enqueueNotifications(firstPage?.notifications || []);
  }, [enqueueNotifications, unreadNotificationsData]);

  useEffect(() => {
    if (!activeLeagueSlug || !socketCtx?.subscribe) return undefined;

    return socketCtx.subscribe('notification:new', (notification) => {
      enqueueNotifications([notification]);
    });
  }, [activeLeagueSlug, enqueueNotifications, socketCtx]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setDocumentVisible(document.hidden !== true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (queue.length === 0 && shownCount !== 0) {
      setShownCount(0);
    }
  }, [queue.length, shownCount]);

  const nextNotificationMatchesLeague = isTransferRevealNotification(queue[0], activeLeagueSlug);
  const currentNotification = !isSquadRevealOpen
    && isDocumentVisible
    && Boolean(currentTeamId)
    && nextNotificationMatchesLeague
    ? queue[0]
    : null;
  const stackTotal = shownCount + queue.length;
  const stackCounter = stackTotal > 1 ? `${shownCount + 1} of ${stackTotal}` : null;

  useSuppressTutorialTips(Boolean(currentNotification));

  const closeCurrentReveal = useCallback(() => {
    const notification = queue[0];
    if (!notification) return;

    markNotificationRead(notification._id);
    setQueue((currentQueue) => currentQueue.filter((_, index) => index !== 0));
    setShownCount((count) => count + 1);
  }, [markNotificationRead, queue]);

  const viewSquad = useCallback(() => {
    const destination = activeLeagueSlug && currentTeamId
      ? `/${activeLeagueSlug}/team-pitch/${currentTeamId}`
      : null;
    closeCurrentReveal();
    if (destination) {
      navigate(destination);
    }
  }, [activeLeagueSlug, closeCurrentReveal, currentTeamId, navigate]);

  const suppressionValue = useMemo(() => ({ setSquadRevealOpen }), []);

  return (
    <TransferRevealSuppressionContext.Provider value={suppressionValue}>
      {children}
      {currentNotification ? (
        <TransferRevealModal
          key={currentNotification._id}
          notification={currentNotification}
          teamName={teamName}
          counter={stackCounter}
          onClose={closeCurrentReveal}
          onViewSquad={viewSquad}
        />
      ) : null}
    </TransferRevealSuppressionContext.Provider>
  );
}

export default TransferRevealProvider;
